/**
 * Strategy-to-Execution Map (V3 screen 17).
 *
 * The single surface that connects a strategic bet to the execution that
 * delivers it: the chain runs
 *
 *   bet → expected outcomes → commitments → deliverables → blockers → decisions
 *
 * It is keyed by the *strategy context* id (the context that hosts the bet
 * package) and renders the read projection produced by the worker's
 * strategy-execution service:
 *
 *   GET /api/contexts/:contextId/strategy-map        (primary)
 *   GET /api/strategies/:contextId/execution-map     (alias — same packet)
 *
 * Lens/projection doctrine (technical/06, `cards-propose-runtime-executes`):
 * this is a READ projection of authorized data. It never mutates business
 * truth, mints policy, or resolves blockers — every navigable action links out
 * to the governed entity (decision / artifact / package), whose own surface
 * carries the server-enforced controls. The screen only reflects state.
 *
 * The body routes through the shared eight-state contract `RenderState` (S13)
 * so loading / empty / error read identically to every other V3 screen, and so
 * QA can inspect each state without hidden assumptions. When the backing read
 * is unreachable (404/405 — e.g. the context is not a bet-hosting strategy
 * context yet) the screen degrades to a truthful "proposed" empty state rather
 * than crashing, exactly as the design system mandates.
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { StrategyExecutionPacket } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { EmptyState } from "@/components/ui/states";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatDateTime, formatNumber, humanize } from "@/lib/format";

/* The shared schema file does not export the inner summary types as named
 * aliases (only `StrategyExecutionPacket`), so derive the slices we render from
 * the packet shape — single source of truth, no duplicate type definitions. */
type StrategyCommitmentSummary = StrategyExecutionPacket["commitments"][number];
type StrategyObjectiveSummary = StrategyExecutionPacket["expected_outcomes"][number];
type StrategyKeyResultSummary = StrategyCommitmentSummary["key_results"][number];
type StrategyBlocker = StrategyCommitmentSummary["blockers"][number];
type StrategyClaimSummary = StrategyExecutionPacket["claims"][number];
type StrategyPrioritizedPackage = StrategyExecutionPacket["prioritized_packages"][number];
type StrategyMovementStatus = StrategyCommitmentSummary["movement_status"];

/* ------------------------------------------------------------------ data load */

interface StrategyMapState {
  data: StrategyExecutionPacket | null;
  loading: boolean;
  error: string | null;
  /** `true` when the read projection is unreachable (404/405) → proposed. */
  proposed: boolean;
  reload: () => void;
}

function useStrategyExecutionPacket(contextId: string): StrategyMapState {
  const [data, setData] = React.useState<StrategyExecutionPacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [proposed, setProposed] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProposed(false);
    api
      .get<StrategyExecutionPacket>(
        `/contexts/${encodeURIComponent(contextId)}/strategy-map`,
      )
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 404/405 → no strategy-map projection for this context yet (proposed).
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          setData(null);
          setProposed(true);
          return;
        }
        setError(
          err instanceof Error ? err.message : "Failed to load the strategy map.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextId, reloadKey]);

  return { data, loading, error, proposed, reload };
}

/* --------------------------------------------------------------- movement tone */

/** Map the derived movement status to a StatusBadge tone string. */
const MOVEMENT_STATUS: Record<StrategyMovementStatus, string> = {
  unknown: "draft",
  on_track: "active",
  at_risk: "needs_review",
  blocked: "blocked",
  stale: "needs_review",
};

function MovementBadge({ status }: { status: StrategyMovementStatus }) {
  return <StatusBadge status={MOVEMENT_STATUS[status]} label={humanize(status)} />;
}

/* ----------------------------------------------------------------- small parts */

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function ProgressRatio({ ratio }: { ratio: number | null }) {
  if (ratio === null) return <span className="text-muted-foreground">—</span>;
  return <span className="font-mono tabular-nums">{Math.round(ratio * 100)}%</span>;
}

function KeyResultRow({ kr }: { kr: StrategyKeyResultSummary }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="[overflow-wrap:anywhere]">{kr.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">
            {formatNumber(kr.current_value)}
            {kr.target_value === null ? "" : ` / ${formatNumber(kr.target_value)}`}
            {kr.unit ? ` ${kr.unit}` : ""}
          </span>
          {kr.metric ? (
            <RefLink resource="metrics" id={kr.metric_id} label={kr.metric.name} />
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <ProgressRatio ratio={kr.progress_ratio} />
        <MovementBadge status={kr.movement_status} />
      </div>
    </div>
  );
}

function BlockerRow({ blocker }: { blocker: StrategyBlocker }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon name="reject" className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {blocker.label}
        <span className="ml-2 text-xs text-muted-foreground">
          {humanize(blocker.kind)} ·{" "}
          {blocker.subject_type === "decision" ? (
            <RefLink resource="decisions" id={blocker.subject_id} />
          ) : (
            <span className="font-mono">{blocker.subject_id}</span>
          )}
        </span>
      </span>
    </li>
  );
}

function CommitmentCard({ commitment }: { commitment: StrategyCommitmentSummary }) {
  const { objective } = commitment;
  return (
    <Card data-testid="strategy-commitment-card">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">
              {objective.title}
            </CardTitle>
            {objective.description ? (
              <p className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {objective.description}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ProgressRatio ratio={commitment.progress_ratio} />
            <MovementBadge status={commitment.movement_status} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4 pt-0">
        <FieldGrid>
          <Field label="Owner contexts">
            {commitment.owner_contexts.length === 0 ? (
              "—"
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {commitment.owner_contexts.map((owner) => (
                  <RefLink key={owner.id} resource="contexts" id={owner.id} label={owner.name} />
                ))}
              </div>
            )}
          </Field>
          <Field label="Period">{objective.period ?? "—"}</Field>
          <Field label="Status">
            <StatusBadge status={objective.status} />
          </Field>
          <Field label="Binding">
            <span className="font-mono text-xs">{objective.strategic_binding_id}</span>
          </Field>
        </FieldGrid>

        {commitment.key_results.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-medium text-muted-foreground">Key results</div>
            {commitment.key_results.map((kr) => (
              <KeyResultRow key={kr.id} kr={kr} />
            ))}
          </div>
        ) : null}

        {commitment.deliverables.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-medium text-muted-foreground">Deliverables</div>
            <div className="flex flex-wrap gap-2">
              {commitment.deliverables.map((artifact) => (
                <Badge key={artifact.id} variant="outline" className="gap-1.5">
                  <Icon name="artifacts" aria-hidden />
                  <RefLink resource="artifacts" id={artifact.id} label={artifact.title} />
                  <StatusBadge status={artifact.status} />
                </Badge>
              ))}
            </div>
          </div>
        ) : null}

        {commitment.decisions.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-medium text-muted-foreground">Decisions</div>
            <div className="flex flex-wrap gap-2">
              {commitment.decisions.map((decision) => (
                <Badge key={decision.id} variant="outline" className="gap-1.5">
                  <Icon name="decisions" aria-hidden />
                  <RefLink resource="decisions" id={decision.id} label={decision.title} />
                  <StatusBadge status={decision.status} />
                </Badge>
              ))}
            </div>
          </div>
        ) : null}

        {commitment.blockers.length > 0 ? (
          <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Icon name="reject" aria-hidden />
              Blockers
            </div>
            <ul className="flex flex-col gap-1.5">
              {commitment.blockers.map((blocker) => (
                <BlockerRow key={`${blocker.kind}:${blocker.subject_id}`} blocker={blocker} />
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function OutcomeCard({ outcome }: { outcome: StrategyObjectiveSummary }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-base [overflow-wrap:anywhere]">
            {outcome.title}
          </CardTitle>
          <StatusBadge status={outcome.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {outcome.description ? (
          <p className="mb-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {outcome.description}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {outcome.period ? <Badge variant="secondary">{outcome.period}</Badge> : null}
          <span className="font-mono">{outcome.id}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function PrioritizedPackageCard({ edge }: { edge: StrategyPrioritizedPackage }) {
  const target = edge.target_package;
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-base [overflow-wrap:anywhere]">
            {target.name}
          </CardTitle>
          <StatusBadge status={target.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{humanize(target.package_type)}</Badge>
          <Badge variant="secondary">{humanize(target.target_level)}</Badge>
          <RefLink resource="packages" id={target.id} label="Open package" />
        </div>
      </CardContent>
    </Card>
  );
}

function ClaimCard({ claim }: { claim: StrategyClaimSummary }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-base [overflow-wrap:anywhere]">
            {claim.title}
          </CardTitle>
          <StatusBadge status={claim.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{humanize(claim.claim_type)}</Badge>
          {claim.confidence_score === null ? null : (
            <Badge variant="secondary">Confidence {claim.confidence_score}</Badge>
          )}
          <Badge variant="secondary">
            {claim.accepted_evidence_count} accepted evidence
          </Badge>
          <RefLink resource="claims" id={claim.id} />
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------- the body */

function StrategyMapContent({
  packet,
  reload,
}: {
  packet: StrategyExecutionPacket;
  reload: () => void;
}) {
  const { summary, bet, strategic_context: context } = packet;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Metric label="Expected outcomes" value={summary.expected_outcomes} />
        <Metric label="Commitments" value={summary.commitments} />
        <Metric label="Key results" value={summary.key_results} />
        <Metric label="Prioritized" value={summary.prioritized_packages} />
        <Metric label="Deliverables" value={summary.deliverables} />
        <Metric label="Decisions" value={summary.decisions} />
        <Metric label="Blockers" value={summary.blockers} />
        <Metric label="Stale" value={summary.stale_commitments} />
      </div>

      <InspectorSection
        title="Bet"
        aside={
          <span className="text-xs text-muted-foreground">
            Generated {formatDateTime(packet.generated_at)} · window {humanize(packet.window)}
          </span>
        }
      >
        <Card data-testid="strategy-bet-card">
          <CardHeader className="p-4 pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <CardTitle className="text-lg [overflow-wrap:anywhere]">
                <Icon name="bets" data-icon="inline-start" aria-hidden />
                {bet.name}
              </CardTitle>
              <StatusBadge status={bet.status} />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {bet.thesis ? (
              <p className="mb-3 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {bet.thesis}
              </p>
            ) : null}
            <FieldGrid>
              <Field label="Strategy context">
                <RefLink resource="contexts" id={context.id} label={context.name} />
              </Field>
              <Field label="Target level">{humanize(bet.target_level)}</Field>
              <Field label="Maturity">{humanize(bet.maturity_stage)}</Field>
              <Field label="Confidentiality">{humanize(context.confidentiality)}</Field>
              <Field label="Package">
                <RefLink resource="packages" id={bet.id} label={bet.name} />
              </Field>
              <Field label="Context status">
                <StatusBadge status={context.status} />
              </Field>
            </FieldGrid>
          </CardContent>
        </Card>
      </InspectorSection>

      <InspectorSection
        title="Expected Outcomes"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        {packet.expected_outcomes.length === 0 ? (
          <EmptyState
            icon="okrs"
            title="No expected outcomes bound to this bet."
            hint="Bind objectives to the strategy context to populate the strategy-to-execution chain."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {packet.expected_outcomes.map((outcome) => (
              <OutcomeCard key={outcome.id} outcome={outcome} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Commitments">
        {packet.commitments.length === 0 ? (
          <EmptyState
            icon="okrs"
            title="No owned commitments yet."
            hint="A commitment is an expected outcome an execution context has taken ownership of via a binding."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {packet.commitments.map((commitment) => (
              <CommitmentCard key={commitment.objective.id} commitment={commitment} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Prioritized Packages">
        {packet.prioritized_packages.length === 0 ? (
          <EmptyState
            icon="packages"
            title="No prioritized packages."
            hint="Packages this bet prioritizes (downstream edges from the bet) appear here."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {packet.prioritized_packages.map((edge) => (
              <PrioritizedPackageCard key={edge.edge_id} edge={edge} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Claims">
        {packet.claims.length === 0 ? (
          <EmptyState
            icon="evidence"
            title="No claims on this bet."
            hint="Claims attached to the bet and its deliverables surface here with their accepted-evidence count."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {packet.claims.map((claim) => (
              <ClaimCard key={claim.id} claim={claim} />
            ))}
          </div>
        )}
      </InspectorSection>
    </div>
  );
}

/* ------------------------------------------------------------------- the route */

export function StrategyMapRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <RenderState state="error" errorMessage="Missing strategy context id." />;
  return <StrategyMapPage contextId={id} />;
}

function StrategyMapPage({ contextId }: { contextId: string }) {
  const go = useGo();
  const { data, loading, error, proposed, reload } = useStrategyExecutionPacket(contextId);

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : data
        ? "normal"
        : "empty";

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Strategy-to-Execution Map"
          subtitle="Connect a strategic bet to the execution that delivers it: expected outcomes, owned commitments, deliverables, blockers, and decisions."
          icon="bets"
          actions={
            <Button
              variant="outline"
              onClick={() => go({ to: `/contexts/${contextId}` })}
            >
              <Icon name="contexts" data-icon="inline-start" aria-hidden />
              Context
            </Button>
          }
        />
        <RenderState
          state={state}
          loadingRows={6}
          error={error}
          errorMessage={error ?? undefined}
          onRetry={reload}
          empty={{
            icon: "bets",
            title: proposed
              ? "No strategy-map projection for this context"
              : "No strategy map available",
            hint: proposed
              ? "This context is not a bet-hosting strategy context, or the strategy-execution projection is not yet built for it."
              : "Open this screen from a strategy context that hosts a bet package.",
          }}
        >
          {data ? <StrategyMapContent packet={data} reload={reload} /> : null}
        </RenderState>
      </div>
    </div>
  );
}

export default StrategyMapRoute;
